import { useClipboard } from "@mantine/hooks";
import cx from "classnames";
import type { ReactNode } from "react";
import { Fragment, forwardRef, useCallback, useMemo, useState } from "react";
import { match } from "ts-pattern";
import { t } from "ttag";

import { useSubmitMetabotFeedbackMutation } from "metabase/api/metabot";
import { useToast } from "metabase/common/hooks";
import { MetabotManagedProviderLimitActions } from "metabase/metabot/components/MetabotManagedProviderLimit";
import {
  type MetabotAgentChatMessage,
  type MetabotAgentDataPartMessage,
  type MetabotAgentId,
  type MetabotAgentTextChatMessage,
  type MetabotAgentTurnError,
  type MetabotAgentTurnErroredMessage,
  type MetabotChatMessage,
  type MetabotDataPart,
  type MetabotDebugToolCallMessage,
  type MetabotUserChatMessage,
  forkConversation,
  isChainOfThoughtMessage,
} from "metabase/metabot/state";
import { useDispatch } from "metabase/redux";
import { useSetting } from "metabase/settings";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Flex,
  type FlexProps,
  Icon,
  Loader,
  Text,
  Tooltip,
} from "metabase/ui";
import type { IconName, MetabotFeedback } from "metabase-types/api";

import { AIMarkdown } from "../AIMarkdown/AIMarkdown";

import { AgentDataPartMessage } from "./MetabotAgentDataPartMessage";
import { AgentToolCallMessage } from "./MetabotAgentToolCallMessage";
import {
  MetabotChainOfThought,
  MetabotToolProgress,
} from "./MetabotChainOfThought";
import Styles from "./MetabotChat.module.css";
import { MetabotFeedbackModal } from "./MetabotFeedbackModal";

const isUserVisibleDataPart = (part: MetabotDataPart): boolean =>
  match(part)
    .with({ type: "data-todo_list" }, () => true)
    .with({ type: "data-transform_suggestion" }, () => true)
    .with({ type: "data-navigate_to" }, () => true)
    .with({ type: "data-code_edit" }, () => true)
    .with({ type: "data-generated_entity" }, () => true)
    .with({ type: "data-entity_saved" }, () => true)
    .with({ type: "data-adhoc_viz" }, () => false)
    .with({ type: "data-static_viz" }, () => false)
    .exhaustive();

const isUserVisibleDataPartMessage = (
  message: MetabotAgentDataPartMessage,
): boolean =>
  match(message)
    .with({ part: { type: "data-code_edit" } }, ({ metadata }) => {
      return metadata?.codeEditBuffer?.source.database_id != null;
    })
    .otherwise(({ part }) => isUserVisibleDataPart(part));

const isUserVisibleMessage = (message: MetabotChatMessage): boolean =>
  match(message)
    .with({ type: "text" }, () => true)
    .with({ type: "data_part" }, (message) =>
      isUserVisibleDataPartMessage(message),
    )
    .with({ type: "tool_call" }, () => false)
    .with({ type: "chain_of_thought" }, () => true)
    .with({ type: "turn_aborted" }, () => true)
    .with({ type: "turn_errored" }, () => true)
    .with({ type: "turn_in_progress" }, () => false)
    .exhaustive();

const isConversationContent = (message: MetabotChatMessage) =>
  !isChainOfThoughtMessage(message) && message.type !== "tool_call";

interface BaseMessageProps extends Omit<FlexProps, "onCopy"> {
  message: MetabotChatMessage;
  hideActions: boolean;
}

export const MessageContainer = ({
  chatRole,
  className,
  ...props
}: FlexProps & {
  chatRole: MetabotChatMessage["role"];
}) => (
  <Flex
    className={cx(
      Styles.messageContainer,
      chatRole === "user"
        ? Styles.messageContainerUser
        : Styles.messageContainerAgent,
      className,
    )}
    data-message-role={chatRole}
    direction="column"
    {...props}
  />
);

interface UserMessageProps extends Omit<BaseMessageProps, "message"> {
  message: MetabotUserChatMessage;
  extraActions?: ReactNode;
}

export const UserMessage = ({
  message,
  className,
  hideActions,
  extraActions,
  ...props
}: UserMessageProps) => {
  const clipboard = useClipboard({ timeout: 2000 });

  return (
    <MessageContainer chatRole={message.role} {...props}>
      {message.type === "text" && (
        <AIMarkdown
          className={cx(Styles.message, Styles.messageUser)}
          singleNewlinesAreParagraphs
        >
          {message.message}
        </AIMarkdown>
      )}

      <Flex className={Styles.messageActions}>
        {!hideActions && (
          <Tooltip label={clipboard.copied ? t`Copied!` : t`Copy`}>
            <ActionIcon
              h="sm"
              data-testid="metabot-chat-message-copy"
              onClick={() => clipboard.copy(message.message)}
            >
              <Icon name="copy" size="1rem" />
            </ActionIcon>
          </Tooltip>
        )}
        {extraActions}
      </Flex>
    </MessageContainer>
  );
};

interface FeedbackButtonProps {
  disabled: boolean;
  icon: IconName;
  onClick: () => void;
  hasBeenClicked: boolean;
}

const FeedbackButton = forwardRef<HTMLButtonElement, FeedbackButtonProps>(
  function FeedbackButton(
    { disabled, icon, onClick, hasBeenClicked, ...props },
    ref,
  ) {
    return (
      <ActionIcon
        onClick={onClick}
        disabled={disabled}
        h="sm"
        {...props}
        ref={ref}
      >
        <Icon
          name={icon}
          size="1rem"
          c={hasBeenClicked ? "core-brand" : "currentColor"}
        />
      </ActionIcon>
    );
  },
);

interface AgentMessageProps extends Omit<BaseMessageProps, "message"> {
  message: MetabotAgentChatMessage;
  debug: boolean;
  readonly: boolean;
  conversationId: string;
  onRetry?: (messageId: string) => void;
  onRefreshConversation?: () => void;
  getCopyText: () => string;
  setFeedbackMessage?: (data: { messageId: string; positive: boolean }) => void;
  submittedFeedback: "positive" | "negative" | undefined;
  onInternalLinkClick?: (link: string) => void;
  extraActions?: ReactNode;
  isStreaming?: boolean;
  supportsReasoning?: boolean;
  onFork?: (messageId: string) => void;
  isForking?: boolean;
  onToolCallSelect?: (message: MetabotDebugToolCallMessage) => void;
}

export const AgentMessage = ({
  message,
  className,
  debug,
  readonly,
  conversationId,
  getCopyText,
  onRetry,
  onRefreshConversation,
  setFeedbackMessage,
  submittedFeedback,
  onInternalLinkClick,
  hideActions,
  extraActions,
  isStreaming = false,
  supportsReasoning = true,
  onFork,
  isForking,
  onToolCallSelect,
  ...props
}: AgentMessageProps) => {
  const messageId = "externalId" in message ? (message.externalId ?? "") : "";
  const isInProgress = message.type === "turn_in_progress";
  const isFailedTurn =
    message.type === "turn_errored" || message.type === "turn_aborted";
  const canActOnMessage = !readonly && !isInProgress && !!messageId;
  const canGiveFeedback = canActOnMessage && !!setFeedbackMessage;
  const canFork = canActOnMessage && !isFailedTurn && !!onFork;
  const clipboard = useClipboard({ timeout: 2000 });

  return (
    <MessageContainer chatRole={message.role} {...props}>
      {match(message)
        .with({ type: "text" }, (m) => (
          <AIMarkdown
            className={Styles.message}
            onInternalLinkClick={onInternalLinkClick}
            isStreaming={isStreaming}
          >
            {m.message}
          </AIMarkdown>
        ))
        .with({ type: "data_part" }, (m) => (
          <AgentDataPartMessage
            message={m}
            debug={debug}
            readonly={readonly}
            conversationId={conversationId}
          />
        ))
        .with({ type: "tool_call" }, (m) => (
          <AgentToolCallMessage message={m} onSelect={onToolCallSelect} />
        ))
        .with({ type: "chain_of_thought" }, (m) =>
          supportsReasoning ? (
            <MetabotChainOfThought message={m} isStreaming={isStreaming} />
          ) : (
            <MetabotToolProgress message={m} isStreaming={isStreaming} />
          ),
        )
        .with({ type: "turn_aborted" }, (m) => (
          <AbortedTurnAlert messageId={m.id} debug={debug} onRetry={onRetry} />
        ))
        .with({ type: "turn_errored" }, (m) => (
          <AgentErroredTurnAlert
            message={m}
            debug={debug}
            onRefreshConversation={onRefreshConversation}
          />
        ))
        .with({ type: "turn_in_progress" }, () => (
          <Loader
            type="dots"
            size="lg"
            color="core-brand"
            data-testid="metabot-response-loader"
          />
        ))
        .exhaustive()}
      {!hideActions && (
        <Flex className={Styles.messageActions} align="center">
          {!isInProgress && (
            <Tooltip label={clipboard.copied ? t`Copied!` : t`Copy`}>
              <ActionIcon
                h="sm"
                data-testid="metabot-chat-message-copy"
                onClick={() => clipboard.copy(getCopyText())}
              >
                <Icon name="copy" size="1rem" />
              </ActionIcon>
            </Tooltip>
          )}
          {canGiveFeedback && (
            <>
              <Tooltip label={t`Give positive feedback`}>
                <FeedbackButton
                  data-testid="metabot-chat-message-thumbs-up"
                  icon="thumbs_up"
                  hasBeenClicked={submittedFeedback === "positive"}
                  disabled={!!submittedFeedback}
                  onClick={() =>
                    setFeedbackMessage({ messageId, positive: true })
                  }
                />
              </Tooltip>
              <Tooltip label={t`Give negative feedback`}>
                <FeedbackButton
                  data-testid="metabot-chat-message-thumbs-down"
                  icon="thumbs_down"
                  hasBeenClicked={submittedFeedback === "negative"}
                  disabled={!!submittedFeedback}
                  onClick={() =>
                    setFeedbackMessage({ messageId, positive: false })
                  }
                />
              </Tooltip>
            </>
          )}
          {onRetry && (
            <Tooltip label={t`Retry`}>
              <ActionIcon
                onClick={() => onRetry(message.id)}
                h="sm"
                data-testid="metabot-chat-message-retry"
              >
                <Icon name="revert" size="1rem" />
              </ActionIcon>
            </Tooltip>
          )}
          {extraActions}
          {canFork && (
            <Tooltip label={t`Fork conversation`}>
              <ActionIcon
                h="sm"
                data-testid="metabot-chat-message-fork"
                loading={isForking}
                disabled={isForking}
                onClick={() => onFork(messageId)}
              >
                <Icon name="git_branch" size="1rem" />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      )}
    </MessageContainer>
  );
};

const AgentTurnAlert = ({
  variant,
  message,
  cta,
  footer,
  debugDetails,
}: {
  variant: "error" | "info";
  message: string;
  cta?: ReactNode;
  footer?: ReactNode;
  debugDetails?: MetabotAgentTurnError;
}) => (
  <Flex
    direction="column"
    gap="xs"
    p="sm"
    bd="1px solid var(--mb-color-border-neutral)"
    bdrs="sm"
    data-testid="metabot-chat-message-turn-alert"
    bg="background_page-primary"
  >
    <Flex align="center" gap="sm">
      <Icon
        name={variant === "error" ? "warning" : "info"}
        c={variant === "error" ? "feedback-negative" : "text-secondary"}
        size="1rem"
        flex="0 0 auto"
      />
      <Text c="text-secondary" size="sm" flex="1">
        {message}
      </Text>
      {cta}
    </Flex>
    {debugDetails && (
      <Card
        bdrs="xs"
        ml="lg"
        p="sm"
        withBorder
        shadow="none"
        c="text-secondary"
        fz="xs"
        ff="monospace"
        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        data-testid="metabot-chat-message-turn-alert-debug"
      >
        {JSON.stringify(debugDetails, null, 2)}
      </Card>
    )}
    {footer && <Box ml="lg">{footer}</Box>}
  </Flex>
);

const AgentErroredTurnAlert = ({
  message,
  debug,
  onRefreshConversation,
}: {
  message: MetabotAgentTurnErroredMessage;
  debug: boolean;
  onRefreshConversation?: () => void;
}) => {
  const isOutOfSync = message.error.type === "conversation_out_of_sync";

  return (
    <AgentTurnAlert
      variant="error"
      message={message.display?.message ?? t`Something went wrong`}
      cta={
        isOutOfSync && onRefreshConversation ? (
          <Button
            variant="default"
            size="compact-xs"
            fz="xs"
            onClick={onRefreshConversation}
            data-testid="metabot-chat-message-refresh"
          >
            {t`Refresh`}
          </Button>
        ) : undefined
      }
      footer={
        message.error.type === "metabase_ai_managed_locked" && (
          <MetabotManagedProviderLimitActions inline />
        )
      }
      debugDetails={debug ? message.error : undefined}
    />
  );
};

const AbortedTurnAlert = ({
  messageId,
  debug,
  onRetry,
}: {
  messageId: string;
  debug: boolean;
  onRetry?: (messageId: string) => void;
}) => {
  const metabotName = useSetting("metabot-name");
  return (
    <AgentTurnAlert
      variant="info"
      message={t`Response from ${metabotName} was interrupted`}
      cta={
        !debug && onRetry ? (
          <Button
            variant="default"
            size="compact-xs"
            fz="xs"
            onClick={() => onRetry(messageId)}
            data-testid="metabot-chat-message-retry"
          >
            {t`Retry`}
          </Button>
        ) : null
      }
    />
  );
};

export const getFullAgentReply = (
  messages: MetabotChatMessage[],
  messageId: string,
) => {
  const messageIndex = messages.findLastIndex((m) => m.id === messageId);
  const message = messages[messageIndex];
  if (!message) {
    return [];
  }

  if (message.role === "user") {
    console.warn("getFullAgentReply requires a user message id");
    return [];
  }

  const firstMessageIndex =
    (messages.slice(0, messageIndex).findLastIndex((m) => m.role === "user") ??
      0) + 1;
  const lastMessageIndex =
    messageIndex +
    Math.max(
      messages.slice(messageIndex).findIndex((m) => m.role === "user"),
      0,
    );

  return messages.slice(firstMessageIndex, lastMessageIndex + 1);
};

export const Messages = ({
  messages,
  onRetryMessage,
  onRefreshConversation,
  isDoingScience,
  supportsReasoning = true,
  debug,
  readonly = false,
  agentId,
  conversationId,
  onInternalLinkClick,
  getExtraActions,
  renderAfterMessage,
  onToolCallSelect,
}: {
  messages: MetabotChatMessage[];
  onRetryMessage?: (messageId: string) => void;
  onRefreshConversation?: () => void;
  isDoingScience: boolean;
  supportsReasoning?: boolean;
  debug: boolean;
  readonly?: boolean;
  agentId?: MetabotAgentId;
  conversationId: string;
  onInternalLinkClick?: (navigateToPath: string) => void;
  getExtraActions?: (messageId: string) => ReactNode;
  renderAfterMessage?: (message: MetabotChatMessage) => ReactNode;
  onToolCallSelect?: (message: MetabotDebugToolCallMessage) => void;
}) => {
  const dispatch = useDispatch();
  const visibleMessages = useMemo(
    () => (debug ? messages : messages.filter(isUserVisibleMessage)),
    [debug, messages],
  );
  const lastUserIndex = useMemo(
    () => visibleMessages.findLastIndex((m) => m.role === "user"),
    [visibleMessages],
  );
  const [sendToast] = useToast();
  const [forkingMessageId, setForkingMessageId] = useState<string | null>(null);

  const handleFork = useCallback(
    async (messageId: string) => {
      if (!agentId || isDoingScience) {
        return;
      }
      setForkingMessageId(messageId);
      try {
        await dispatch(
          forkConversation({ agentId, conversationId, messageId }),
        ).unwrap();
        sendToast({ icon: "check", message: t`Conversation forked` });
      } catch {
        sendToast({ icon: "warning", message: t`Failed to fork conversation` });
      } finally {
        setForkingMessageId(null);
      }
    },
    [dispatch, agentId, conversationId, sendToast, isDoingScience],
  );

  const [feedbackState, setFeedbackState] = useState<{
    submitted: Record<string, "positive" | "negative" | undefined>;
    modal: { messageId: string; positive: boolean } | undefined;
  }>({
    submitted: {},
    modal: undefined,
  });

  const [submitMetabotFeedback] = useSubmitMetabotFeedbackMutation();

  const submitFeedback = async (metabotFeedback: MetabotFeedback) => {
    const { message_id, positive } = metabotFeedback;

    try {
      await submitMetabotFeedback(metabotFeedback).unwrap();
      sendToast({ icon: "check", message: t`Feedback submitted` });

      setFeedbackState((prevState) => ({
        submitted: {
          ...prevState.submitted,
          [message_id]: positive ? "positive" : "negative",
        },
        modal: undefined,
      }));
    } catch (error) {
      sendToast({ icon: "warning", message: t`Failed to submit feedback` });
    }
  };

  const getAgentReplyCopyText = useCallback(
    (messageId: string) => {
      const allMessages = getFullAgentReply(messages, messageId);
      const textMessages = allMessages.filter(
        (msg): msg is MetabotAgentTextChatMessage =>
          msg.role === "agent" && msg.type === "text",
      );
      return textMessages.map((msg) => msg.message).join("\n\n");
    },
    [messages],
  );

  return (
    <>
      {visibleMessages.map((message, index) => {
        const next = visibleMessages[index + 1];
        const nextContent = visibleMessages
          .slice(index + 1)
          .find(isConversationContent);
        const isLastUserMessage = index > lastUserIndex;

        const messageElement =
          message.role === "agent" ? (
            <AgentMessage
              data-testid="metabot-chat-message"
              message={message}
              debug={debug}
              readonly={readonly}
              conversationId={conversationId}
              onRetry={isLastUserMessage ? onRetryMessage : undefined}
              onRefreshConversation={onRefreshConversation}
              getCopyText={() => getAgentReplyCopyText(message.id)}
              setFeedbackMessage={(data) =>
                setFeedbackState((prev) => ({ ...prev, modal: data }))
              }
              submittedFeedback={
                "externalId" in message && message.externalId
                  ? feedbackState.submitted[message.externalId]
                  : undefined
              }
              hideActions={
                isChainOfThoughtMessage(message) ||
                nextContent?.role === "agent" ||
                (isDoingScience && !nextContent)
              }
              extraActions={getExtraActions?.(message.id)}
              onFork={
                agentId && !readonly && !isDoingScience ? handleFork : undefined
              }
              isForking={
                "externalId" in message &&
                !!message.externalId &&
                forkingMessageId === message.externalId
              }
              onInternalLinkClick={onInternalLinkClick}
              supportsReasoning={supportsReasoning}
              onToolCallSelect={onToolCallSelect}
              isStreaming={
                isChainOfThoughtMessage(message)
                  ? isDoingScience && !nextContent
                  : isDoingScience && !next
              }
            />
          ) : (
            <UserMessage
              data-testid="metabot-chat-message"
              message={message}
              hideActions={
                isDoingScience && visibleMessages.length === index + 1
              }
              extraActions={getExtraActions?.(message.id)}
            />
          );

        return (
          <Fragment key={"msg-" + message.id}>
            {messageElement}
            {renderAfterMessage?.(message)}
          </Fragment>
        );
      })}

      {feedbackState.modal && (
        <MetabotFeedbackModal
          {...feedbackState.modal}
          onClose={() =>
            setFeedbackState((prev) => ({ ...prev, modal: undefined }))
          }
          onSubmit={submitFeedback}
        />
      )}
    </>
  );
};
